import type { RechargeSubscription } from "../types";
import { fmtDate } from "./format";

export function normalizeSubscriptions(payload: any): RechargeSubscription[] {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.subscriptions)) return payload.subscriptions;
  if (Array.isArray(payload?.data?.subscriptions))
    return payload.data.subscriptions;
  return [];
}

export function isActiveSub(s?: RechargeSubscription) {
  return String(s?.status || "").toLowerCase() === "active";
}

export function getUpcomingNextCharge(bootstrap: any) {
  const u = bootstrap?.upcoming;
  const next =
    u?.next_charge_scheduled_at ||
    u?.next_charge_date ||
    u?.date ||
    u?.charges?.[0]?.scheduled_at ||
    u?.charges?.[0]?.scheduled_at;

  return next ? fmtDate(next) : null;
}

/**
 * Extract __qivora_delivery_date from a Recharge attribute array.
 * Only reads the qivora key — no legacy keys. If missing, callers use estimateDeliveryDate.
 */
export function extractQivoraDeliveryDate(attrs: any[]): string | null {
  if (!Array.isArray(attrs)) return null;
  const hit = attrs.find((a: any) => a.name === "__qivora_delivery_date");
  return hit?.value ? String(hit.value) : null;
}

const DAY_NAME_TO_INDEX: Record<string, number> = {
  sunday: 0, monday: 1, tuesday: 2, wednesday: 3,
  thursday: 4, friday: 5, saturday: 6,
};

/**
 * Estimate delivery date from charge date + cutoff, snapped to the next allowed delivery day.
 * If charge + cutoff lands on Wednesday but allowed days are [Tuesday, Thursday], returns Thursday.
 */
export function estimateDeliveryDate(
  chargeDate: string,
  cutoffHours: number,
  deliveryDayNames: string[]
): string | null {
  if (!chargeDate) return null;
  try {
    const charge = new Date(chargeDate);
    charge.setHours(charge.getHours() + cutoffHours);

    // Convert day names to JS day indices (0=Sun..6=Sat)
    const allowedDays = new Set(
      deliveryDayNames
        .map(n => DAY_NAME_TO_INDEX[n.toLowerCase()])
        .filter(d => d !== undefined)
    );

    // If no allowed days configured, just return charge + cutoff
    if (allowedDays.size === 0) {
      return charge.toISOString().split("T")[0];
    }

    // Snap forward to the next allowed delivery day (max 7 days ahead)
    for (let i = 0; i < 7; i++) {
      const candidate = new Date(charge);
      candidate.setDate(candidate.getDate() + i);
      if (allowedDays.has(candidate.getDay())) {
        return candidate.toISOString().split("T")[0];
      }
    }

    // Shouldn't happen, but fallback
    return charge.toISOString().split("T")[0];
  } catch {
    return null;
  }
}

/**
 * Get next delivery date derived from charge date.
 * Formula: charge date + cutoff hours → snap to next available delivery day.
 * Charge date is the source of truth (from Recharge subscription).
 * Returns a raw YYYY-MM-DD string — formatting happens at render.
 */
export function getNextDeliveryDate(
  bootstrap: any,
  cutoffHours = 36,
  deliveryDayNames: string[] = []
): string | null {
  // Derive from subscription charge dates (source of truth); subs may have
  // unaligned charge dates, so take the soonest estimate.
  const subs = bootstrap?.subscriptions || [];
  let soonest: string | null = null;
  if (Array.isArray(subs)) {
    for (const sub of subs) {
      if (String(sub?.status || "").toLowerCase() !== "active") continue;
      const charge = sub.next_charge_scheduled_at;
      if (!charge) continue;
      const estimated = estimateDeliveryDate(charge, cutoffHours, deliveryDayNames);
      if (estimated && (!soonest || estimated < soonest)) soonest = estimated;
    }
  }
  if (soonest) return soonest;

  // Fallback: upcoming charges
  const u = bootstrap?.upcoming;
  const chargeDate =
    u?.next_charge_scheduled_at ||
    u?.next_charge_date ||
    u?.date ||
    u?.charges?.[0]?.scheduled_at;

  if (chargeDate) {
    const estimated = estimateDeliveryDate(chargeDate, cutoffHours, deliveryDayNames);
    if (estimated) return estimated;
  }

  return null;
}

export type VariantIndexItem = {
  variantId: string;
  productId: string;
  productTitle: string;
  variantTitle: string; // "600g"
  imageUrl: string | null;
  price: number;
  tags: string[];
};

/**
 * Brand recipe photography injected by sections/react-account.liquid as
 * window.__POP_RECIPE_IMAGES__, keyed by the recipe dog's name (sammy, vinnie,
 * loki, boot, tilly). Preferred over Shopify product images for recipe cards.
 */
export function brandRecipeImageFor(title: string | null | undefined): string | null {
  if (!title) return null;
  const map =
    (typeof window !== "undefined" && (window as any).__POP_RECIPE_IMAGES__) || null;
  if (!map) return null;
  const t = String(title).toLowerCase();
  for (const key of Object.keys(map)) {
    if (t.includes(key)) return String(map[key]);
  }
  return null;
}

/**
 * Normalize image URL to use https:// protocol
 */
function normalizeImageUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  const normalized = String(url).trim();
  // Handle protocol-relative URLs (//domain.com/...)
  if (normalized.startsWith("//")) {
    return `https:${normalized}`;
  }
  return normalized;
}

export function buildVariantIndex(
  products: any[]
): Map<string, VariantIndexItem> {
  const map = new Map<string, VariantIndexItem>();

  for (const pWrap of products || []) {
    const p = pWrap?.product ?? pWrap; // support {product:{...}} or direct product
    if (!p?.variants?.length) continue;

    // Handle both array of strings and object with .src formats
    let fallback: string | null = null;
    if (Array.isArray(p?.image)) {
      fallback = normalizeImageUrl(p.image[0]);
    } else if (Array.isArray(p?.images)) {
      fallback = normalizeImageUrl(p.images[0]);
    } else {
      fallback = normalizeImageUrl(p?.image?.src || p?.images?.[0]?.src);
    }

    // Build images map (handle both formats)
    const imagesById = new Map<number, string>();
    for (const img of p?.images || []) {
      if (typeof img === "string") {
        // If images is array of strings, we can't map by ID
        continue;
      }
      if (img?.id && img?.src) {
        imagesById.set(Number(img.id), normalizeImageUrl(img.src) || "");
      }
    }

    // Handle tags as array or comma-separated string
    let tags: string[] = [];
    if (Array.isArray(p?.tags)) {
      tags = p.tags.map((t: any) => String(t).trim()).filter(Boolean);
    } else {
      tags = String(p?.tags || "")
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean);
    }

    // Recipe products use the brand studio photography over Shopify images
    const brandImage = brandRecipeImageFor(p?.title);

    for (const v of p.variants) {
      const variantId = String(v?.id || "");
      if (!variantId) continue;

      const variantImage =
        brandImage ||
        (v?.image_id ? imagesById.get(Number(v.image_id)) || fallback : fallback);

      map.set(variantId, {
        variantId,
        productId: String(p.id),
        productTitle: String(p.title || ""),
        variantTitle: String(v.title || ""),
        imageUrl: variantImage,
        price: parseFloat(v.price) || 0,
        tags,
      });
    }
  }

  return map;
}

function getProp(sub: any, key: string) {
  return sub?.properties?.find((p: any) => p?.name === key)?.value ?? null;
}

function safeJson<T>(raw: any, fallback: T): T {
  try {
    if (!raw) return fallback;
    return JSON.parse(String(raw)) as T;
  } catch {
    return fallback;
  }
}

export type BundlePlan = {
  kind: "bundle";
  subscriptionId: string;
  productTitle: string;
  variantTitle: string;
  boxVariantId: string | null;
  planDays: number | null;
  packSizeGrams: number | null;
  meals: { variantId: string; count: number }[];
  treats: Record<string, number>;
  /** Allergies from the linked dog profile (_paws_dog_json); used to filter recipe pickers. */
  dogAllergies: string[];
};

export function parseBundlePlan(sub: any): BundlePlan {
  return {
    kind: "bundle",
    subscriptionId: String(sub?.id),
    productTitle: String(sub?.product_title || "Plan"),
    variantTitle: String(sub?.variant_title || ""),
    boxVariantId: getProp(sub, "_paws_box_variant_id")
      ? String(getProp(sub, "_paws_box_variant_id"))
      : sub?.shopify_variant_id
      ? String(sub.shopify_variant_id)
      : null,
    planDays: Number(getProp(sub, "_paws_plan_days") || 0) || null,
    packSizeGrams: Number(getProp(sub, "_paws_pack_size_grams") || 0) || null,
    meals: safeJson(getProp(sub, "_paws_meals_json"), []),
    treats: safeJson(getProp(sub, "_paws_treats_json"), {}),
    dogAllergies: (safeJson(getProp(sub, "_paws_dog_json"), {}) as { allergies?: string[] })?.allergies ?? [],
  };
}

/**
 * Union of allergies across all dog profiles attached to the given
 * subscriptions (_paws_dog_json). Used to filter household-level product
 * pickers (extras/treats) the same way the quiz does.
 */
export function collectDogAllergies(subs: any[]): string[] {
  const out = new Set<string>();
  for (const sub of subs || []) {
    const dog = safeJson(getProp(sub, "_paws_dog_json"), {}) as { allergies?: string[] };
    for (const a of dog?.allergies ?? []) out.add(a);
  }
  return [...out];
}

export function isBundleSub(sub: any) {
  const hasMealsJson = !!getProp(sub, "_paws_meals_json");
  const hasDogJson = !!getProp(sub, "_paws_dog_json");
  return hasMealsJson || hasDogJson;
}

export type RecipeGridItem = VariantIndexItem & {
  selected: boolean;
  count: number;
};

export function buildRecipeGridForBundle(
  plan: BundlePlan,
  variantIndex: Map<string, VariantIndexItem>
): RecipeGridItem[] {
  // Show all recipes from the bundle, gray out ones with 0 count
  const grid: RecipeGridItem[] = [];

  for (const meal of plan.meals) {
    const count = Number(meal.count || 0);
    const variantId = String(meal.variantId);
    const info = variantIndex.get(variantId);

    if (info) {
      grid.push({
        ...info,
        selected: count > 0, // Gray out if count is 0
        count,
      });
    }
  }

  // Sort: selected first, then by product title
  grid.sort((a, b) =>
    Number(b.selected) - Number(a.selected) ||
    a.productTitle.localeCompare(b.productTitle)
  );

  return grid;
}

export function buildTreatItemsFromBundle(
  plan: BundlePlan,
  variantIndex: Map<string, VariantIndexItem>
) {
  return Object.entries(plan.treats || {})
    .map(([variantId, qty]) => ({
      variantId: String(variantId),
      qty: Number(qty || 0),
      info: variantIndex.get(String(variantId)) || null,
    }))
    .filter((x) => x.qty > 0);
}

/**
 * Legacy subscription (pre-migration subscriptions without bundle metadata)
 */
export type LegacySubscription = {
  kind: "legacy";
  subscriptionId: string;
  productTitle: string;
  variantTitle: string;
  variantId: string | null;
  quantity: number;
  price: string;
  /** Allergies from the linked dog profile (_paws_dog_json), when present. */
  dogAllergies: string[];
};

/**
 * Parse a legacy subscription (one without bundle metadata)
 */
export function parseLegacySubscription(sub: any): LegacySubscription {
  return {
    kind: "legacy",
    subscriptionId: String(sub?.id),
    productTitle: String(sub?.product_title || "Product"),
    variantTitle: String(sub?.variant_title || ""),
    variantId: sub?.shopify_variant_id ? String(sub.shopify_variant_id) : null,
    quantity: Number(sub?.quantity || 1),
    price: String(sub?.price || "0.00"),
    dogAllergies: (safeJson(getProp(sub, "_paws_dog_json"), {}) as { allergies?: string[] })?.allergies ?? [],
  };
}

/**
 * Build meal items for legacy subscription (just the main product)
 */
export function buildLegacyMealItems(
  legacy: LegacySubscription,
  variantIndex: Map<string, VariantIndexItem>
): RecipeGridItem[] {
  if (!legacy.variantId) return [];

  const info = variantIndex.get(legacy.variantId);
  if (!info) return [];

  return [
    {
      ...info,
      selected: true,
      count: legacy.quantity,
    },
  ];
}
